import { type FormEvent, useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";

import { api, ApiError, type ChatMessage } from "../lib/api.ts";

interface ToolEvent {
	toolUseId: string;
	toolName: string;
	startedAt: number;
	finishedAt?: number;
	ok?: boolean;
	previewBytes?: number;
	input?: unknown;
	preview?: string;
	expanded?: boolean;
}

interface QuickPrompt {
	label: string;
	prompt: string;
	hint?: string;
}

const QUICK_PROMPTS: QuickPrompt[] = [
	{
		label: "Group morning overview",
		prompt:
			"Утренний обзор группы. Перечисли каждый бизнес из :Business нод (list_businesses), укажи tier, текущий статус (если есть), и главный риск. В конце — 2-3 пункта на что мне обратить внимание сегодня.",
		hint: "list_businesses → структурированный summary",
	},
	{
		label: "Venture bets status",
		prompt:
			"Покажи статус всех венчурных ставок (Mining, Health, Defence). Для каждой: текущий gate, runway, последнее обновление, что нужно от меня. Если статус не записан — отметь явно и предложи внести.",
		hint: "get_business_status × 3 + venture methodology",
	},
	{
		label: "g1.networks last 7 days",
		prompt:
			"Что происходило в g1.networks за последние 7 дней? Покажи clicks, conversions, top partners. Используй query_analytics против events_affiliate_*.",
		hint: "bq_query на BQ events",
	},
	{
		label: "Pending follow-ups",
		prompt:
			"Какие у меня follow-ups сейчас открыты? Перечисли decisions с follow_up_date в будущем или просроченные.",
		hint: "list_decisions due_only=true",
	},
	{
		label: "Top affiliates",
		prompt:
			"Покажи топ-10 партнёров по конверсиям за последние 30 дней. Использ query_state на g1-shared (partners + partner_conversions JOIN).",
		hint: "turso_query",
	},
	{
		label: "Cash position concern",
		prompt:
			"Напомни мне про red flag #1 группы (working capital у G1-Development). Что мы знаем сейчас? Какие действия предложены в governance KB?",
		hint: "search_governance_kb + get_business_status",
	},
];

export function Chat() {
	const [messages, setMessages] = useState<ChatMessage[]>([]);
	const [streamingText, setStreamingText] = useState("");
	const [toolEvents, setToolEvents] = useState<ToolEvent[]>([]);
	const [input, setInput] = useState("");
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [historyLoaded, setHistoryLoaded] = useState(false);
	const scrollRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		// Load server-side history once on mount — survives tab switches and refreshes.
		api
			.getChatHistory()
			.then((res) => {
				if (res.messages.length > 0) setMessages(res.messages);
			})
			.catch(() => {
				// Non-fatal — just start with an empty chat.
			})
			.finally(() => setHistoryLoaded(true));
	}, []);

	useEffect(() => {
		scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
	}, [messages, streamingText]);

	async function handleClear() {
		if (busy) return;
		try {
			await api.clearChatHistory();
		} catch {
			// Best-effort — clear UI even if KV delete failed.
		}
		setMessages([]);
		setError(null);
		setStreamingText("");
		setToolEvents([]);
	}

	async function handleSubmit(e: FormEvent) {
		e.preventDefault();
		const text = input.trim();
		if (!text || busy) return;

		const next: ChatMessage[] = [...messages, { role: "user", content: text }];
		setMessages(next);
		setInput("");
		setBusy(true);
		setError(null);
		setStreamingText("");
		setToolEvents([]);

		let accumulated = "";
		const tools: ToolEvent[] = [];

		try {
			for await (const event of api.chatStream({ messages: next })) {
				if (event.type === "text_delta") {
					accumulated += event.text;
					setStreamingText(accumulated);
				} else if (event.type === "tool_use_start") {
					tools.push({
						toolUseId: event.toolUseId,
						toolName: event.toolName,
						startedAt: Date.now(),
						input: event.input,
					});
					setToolEvents([...tools]);
				} else if (event.type === "tool_use_result") {
					const match = tools.find((t) => t.toolUseId === event.toolUseId);
					if (match) {
						match.finishedAt = Date.now();
						match.ok = event.ok;
						match.previewBytes = event.previewBytes;
						match.preview = event.preview;
					}
					setToolEvents([...tools]);
				} else if (event.type === "error") {
					setError(`${event.code}: ${event.message}`);
				}
			}

			setMessages([...next, { role: "assistant", content: accumulated }]);
		} catch (err) {
			if (err instanceof ApiError) {
				setError(`${err.status}: ${err.message}`);
			} else {
				setError((err as Error).message);
			}
		} finally {
			setBusy(false);
			setStreamingText("");
			setToolEvents([]);
		}
	}

	return (
		<div className="flex flex-col h-full">
			{messages.length > 0 && (
				<div className="border-b border-zinc-800 px-6 py-2 flex items-center justify-end">
					<button
						type="button"
						onClick={handleClear}
						disabled={busy}
						className="text-xs text-zinc-500 hover:text-zinc-300 disabled:opacity-40"
					>
						+ New chat
					</button>
				</div>
			)}
			<div ref={scrollRef} className="flex-1 overflow-y-auto px-6 py-8 space-y-6">
				{messages.length === 0 && !busy && historyLoaded && (
					<div className="max-w-3xl mx-auto mt-16 space-y-8">
						<div className="text-center text-zinc-500">
							<div className="text-2xl font-semibold text-zinc-200 mb-2">G1 Manager</div>
							<div className="text-sm">
								Ask about any of your businesses. I'll apply the right governance methodology
								(operating / venture / board-mode) and pull data from the platform.
							</div>
						</div>
						<div className="space-y-2">
							<div className="text-xs uppercase tracking-wider text-zinc-600 px-1">Quick starts</div>
							<div className="grid grid-cols-1 md:grid-cols-2 gap-2">
								{QUICK_PROMPTS.map((q) => (
									<button
										type="button"
										key={q.label}
										onClick={() => setInput(q.prompt)}
										className="text-left px-3 py-2 rounded-md bg-zinc-900 border border-zinc-800 hover:border-zinc-700 hover:bg-zinc-800/60 transition-colors"
									>
										<div className="text-sm text-zinc-200">{q.label}</div>
										{q.hint && <div className="text-xs text-zinc-500 mt-0.5">{q.hint}</div>}
									</button>
								))}
							</div>
						</div>
					</div>
				)}
				{messages.map((msg, idx) => (
					<MessageBubble key={idx} message={msg} />
				))}

				{toolEvents.length > 0 && (
					<div className="max-w-3xl mx-auto space-y-1.5 text-xs">
						{toolEvents.map((t) => (
							<ToolEventRow
								key={t.toolUseId}
								t={t}
								onToggle={() => {
									t.expanded = !t.expanded;
									setToolEvents([...toolEvents]);
								}}
							/>
						))}
					</div>
				)}

				{streamingText && (
					<div className="max-w-3xl mx-auto flex justify-start">
						<div className="max-w-[80%] px-4 py-3 rounded-lg bg-zinc-900 border border-zinc-800 text-zinc-100">
							<div className="prose prose-invert prose-sm max-w-none">
								<ReactMarkdown>{streamingText}</ReactMarkdown>
							</div>
							<span className="inline-block w-2 h-4 ml-1 bg-zinc-400 animate-pulse" />
						</div>
					</div>
				)}

				{error && (
					<div className="max-w-3xl mx-auto p-3 border border-red-500/30 rounded bg-red-500/10 text-sm text-red-300">
						{error}
					</div>
				)}
			</div>

			<form
				onSubmit={handleSubmit}
				className="border-t border-zinc-800 bg-terminal/50 px-6 py-4"
			>
				<div className="max-w-3xl mx-auto flex gap-3">
					<input
						value={input}
						onChange={(e) => setInput(e.target.value)}
						placeholder="Ask about a business…"
						className="flex-1 px-4 py-2.5 bg-zinc-900 border border-zinc-700 rounded-md text-zinc-100 placeholder:text-zinc-500 focus:outline-none focus:border-accent-500"
						disabled={busy}
					/>
					<button
						type="submit"
						disabled={busy || !input.trim()}
						className="px-4 py-2.5 bg-accent-600 hover:bg-accent-500 disabled:bg-zinc-800 disabled:text-zinc-500 text-white text-sm font-medium rounded-md"
					>
						Send
					</button>
				</div>
			</form>
		</div>
	);
}

function ToolEventRow({ t, onToggle }: { t: ToolEvent; onToggle: () => void }) {
	const inProgress = !t.finishedAt;
	const status = inProgress ? "⋯" : t.ok ? "✓" : "✗";
	const statusColor = inProgress
		? "text-amber-400"
		: t.ok
			? "text-emerald-400"
			: "text-red-400";
	const hasDetails = t.input !== undefined || t.preview !== undefined;

	return (
		<div className="rounded-md bg-zinc-900/60 border border-zinc-800 overflow-hidden">
			<button
				type="button"
				onClick={hasDetails ? onToggle : undefined}
				className={`w-full flex items-center gap-2 px-3 py-1.5 text-left ${hasDetails ? "hover:bg-zinc-800/40 cursor-pointer" : "cursor-default"}`}
			>
				<span className={statusColor}>{status}</span>
				<code className="text-zinc-300">{t.toolName}</code>
				{hasDetails && (
					<span className="text-zinc-600 text-[10px]">{t.expanded ? "▼" : "▶"}</span>
				)}
				{t.finishedAt && (
					<span className="text-zinc-500 ml-auto">
						{((t.finishedAt - t.startedAt) / 1000).toFixed(1)}s
						{t.previewBytes ? ` · ${(t.previewBytes / 1024).toFixed(1)} KB` : ""}
					</span>
				)}
			</button>
			{t.expanded && hasDetails && (
				<div className="px-3 pb-3 pt-1 border-t border-zinc-800/60 space-y-2">
					{t.input !== undefined && (
						<details open className="text-[11px]">
							<summary className="cursor-pointer text-zinc-500 mb-1">input</summary>
							<pre className="bg-terminal/60 rounded p-2 overflow-x-auto text-zinc-300">
								{JSON.stringify(t.input, null, 2)}
							</pre>
						</details>
					)}
					{t.preview !== undefined && (
						<details open className="text-[11px]">
							<summary className="cursor-pointer text-zinc-500 mb-1">
								response preview {t.previewBytes ? `(${t.previewBytes} bytes total)` : ""}
							</summary>
							<pre className="bg-terminal/60 rounded p-2 overflow-x-auto text-zinc-300 whitespace-pre-wrap break-words max-h-64">
								{t.preview}
								{t.previewBytes && t.previewBytes > (t.preview?.length ?? 0) ? "\n…(truncated)" : ""}
							</pre>
						</details>
					)}
				</div>
			)}
		</div>
	);
}

function MessageBubble({ message }: { message: ChatMessage }) {
	const isUser = message.role === "user";
	const text = typeof message.content === "string" ? message.content : "";

	return (
		<div className={`max-w-3xl mx-auto flex ${isUser ? "justify-end" : "justify-start"}`}>
			<div
				className={`max-w-[80%] px-4 py-3 rounded-lg ${
					isUser ? "bg-accent-600 text-white" : "bg-zinc-900 border border-zinc-800 text-zinc-100"
				}`}
			>
				{isUser ? (
					<div className="whitespace-pre-wrap">{text}</div>
				) : (
					<div className="prose prose-invert prose-sm max-w-none">
						<ReactMarkdown>{text}</ReactMarkdown>
					</div>
				)}
			</div>
		</div>
	);
}
